import { Link } from "react-router-dom";
import { Leaf, Mail, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { CookieSettingsLink } from "./CookieBanner";
import TrustBadgeRow from "./TrustBadgeRow";
import LanguagePicker from "./LanguagePicker";
import CountryPill from "./shop/CountryPill";

export default function Footer() {
  const { t } = useI18n();
  const [whatsapp, setWhatsapp] = useState("");
  useEffect(() => {
    api
      .get("/config")
      .then(({ data }) => setWhatsapp(data?.whatsapp_number || ""))
      .catch(() => setWhatsapp(""));
  }, []);
  const waUrl = whatsapp
    ? `https://wa.me/${whatsapp}?text=${encodeURIComponent("Hi Feel Greats team!")}`
    : "https://wa.me/?text=Hi%20Feel%20Greats%20team";

  return (
    <footer className="mt-16 border-t border-emerald-100/60 bg-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-6 gap-10">
          {/* Brand + tagline */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4" data-testid="footer-home-link">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-emerald-700 text-white shadow-sm">
                <Leaf className="w-5 h-5" />
              </span>
              <div className="leading-tight">
                <div className="font-display text-lg font-semibold text-emerald-950">Feel Greats</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-600">
                  Smart health · real results
                </div>
              </div>
            </Link>
            <p className="text-sm text-emerald-900/70 max-w-md leading-relaxed">
              Personalised wellness guidance built around your body, your goals and your real
              life. We help you find the right Unicity Feel Great System products — and stay
              consistent long enough to see real results.
            </p>
            <p className="mt-4 text-xs text-emerald-900/60 max-w-md leading-relaxed">
              <strong>Wellness, not medical advice.</strong> Recommendations on Feel Greats are
              for general wellness and educational purposes only and are not a substitute for
              professional medical advice, diagnosis or treatment. Always consult a qualified
              healthcare professional before starting any supplement regimen — especially if you
              are pregnant, nursing, taking medication or managing a chronic condition. Results
              vary.
            </p>
          </div>

          {/* Legal nav */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-3">
              {t("footer.legal")}
            </div>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/privacy"
                  className="text-emerald-900/80 hover:text-emerald-700 transition-colors"
                  data-testid="footer-privacy-link"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-emerald-900/80 hover:text-emerald-700 transition-colors"
                  data-testid="footer-terms-link"
                >
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <Link
                  to="/refund"
                  className="text-emerald-900/80 hover:text-emerald-700 transition-colors"
                  data-testid="footer-refund-link"
                >
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-emerald-900/80 hover:text-emerald-700 transition-colors"
                  data-testid="footer-contact-link"
                >
                  Contact us
                </Link>
              </li>
            </ul>
          </div>

          {/* Brand pages */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-3">
              Feel Greats
            </div>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/about" className="text-emerald-900/80 hover:text-emerald-700 transition-colors" data-testid="footer-about-link">
                  About us
                </Link>
              </li>
              <li>
                <Link to="/how-it-works" className="text-emerald-900/80 hover:text-emerald-700 transition-colors" data-testid="footer-how-link">
                  How it works
                </Link>
              </li>
              <li>
                <Link to="/why-feelgreats" className="text-emerald-900/80 hover:text-emerald-700 transition-colors" data-testid="footer-why-link">
                  Why Feel Greats
                </Link>
              </li>
              <li>
                <Link to="/mentor-support" className="text-emerald-900/80 hover:text-emerald-700 transition-colors" data-testid="footer-mentor-link">
                  Mentor support
                </Link>
              </li>
              <li>
                <Link to="/science" className="text-emerald-900/80 hover:text-emerald-700 transition-colors" data-testid="footer-science-link">
                  Science & ingredients
                </Link>
              </li>
              <li>
                <Link to="/stories" className="text-emerald-900/80 hover:text-emerald-700 transition-colors" data-testid="footer-stories-link">
                  Member stories
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-emerald-900/80 hover:text-emerald-700 transition-colors" data-testid="footer-faq-link">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          {/* Learn (wellness library) */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-3">
              {t("footer.wellness_library")}
            </div>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/shop"
                  className="text-emerald-900/80 hover:text-emerald-700 transition-colors"
                  data-testid="footer-shop-link"
                >
                  Shop the range
                </Link>
              </li>
              <li>
                <Link
                  to="/learn"
                  className="text-emerald-900/80 hover:text-emerald-700 transition-colors"
                  data-testid="footer-learn-link"
                >
                  {t("footer.all_guides")}
                </Link>
              </li>
              <li>
                <Link to="/learn/insulin-resistance" className="text-emerald-900/80 hover:text-emerald-700 transition-colors">
                  Insulin resistance
                </Link>
              </li>
              <li>
                <Link to="/learn/pcos" className="text-emerald-900/80 hover:text-emerald-700 transition-colors">
                  PCOS
                </Link>
              </li>
              <li>
                <Link to="/learn/menopause" className="text-emerald-900/80 hover:text-emerald-700 transition-colors">
                  Menopause
                </Link>
              </li>
              <li>
                <Link to="/learn/belly-fat" className="text-emerald-900/80 hover:text-emerald-700 transition-colors">
                  Belly fat
                </Link>
              </li>
            </ul>
          </div>

          {/* Get in touch */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-3">
              Get in touch
            </div>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="mailto:support@feelgreats.co.uk"
                  className="flex items-center gap-2 text-emerald-900/80 hover:text-emerald-700 transition-colors"
                  data-testid="footer-email-link"
                >
                  <Mail className="w-4 h-4 shrink-0" /> support@feelgreats.co.uk
                </a>
              </li>
              <li>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex items-center gap-2 text-emerald-900/80 hover:text-emerald-700 transition-colors"
                  data-testid="footer-whatsapp-link"
                >
                  <MessageCircle className="w-4 h-4 shrink-0" /> WhatsApp coach
                </a>
              </li>
              <li>
                <a
                  href="https://feelgreats.co.uk"
                  className="text-emerald-900/60 hover:text-emerald-700 transition-colors text-xs"
                >
                  feelgreats.co.uk
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust badges row */}
        <div className="mt-10 pt-8 border-t border-emerald-100/70">
          <TrustBadgeRow compact />
        </div>

        <div className="mt-8 pt-6 border-t border-emerald-100/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-emerald-900/60">
          <div>{t("footer.rights", { year: new Date().getFullYear() })}</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link to="/privacy" className="hover:text-emerald-700">Privacy</Link>
            <span aria-hidden>·</span>
            <Link to="/terms" className="hover:text-emerald-700">Terms</Link>
            <span aria-hidden>·</span>
            <Link to="/refund" className="hover:text-emerald-700">Refund</Link>
            <span aria-hidden>·</span>
            <Link to="/contact" className="hover:text-emerald-700">Contact</Link>
            <span aria-hidden>·</span>
            <Link to="/data-deletion" className="hover:text-emerald-700" data-testid="footer-data-deletion-link">Delete my data</Link>
            <span aria-hidden>·</span>
            <CookieSettingsLink />
            <span aria-hidden>·</span>
            <LanguagePicker variant="text" />
            <span aria-hidden>·</span>
            <CountryPill variant="text" />
          </div>
        </div>
      </div>
    </footer>
  );
}
